import React, { forwardRef, useId } from 'react';

export interface InputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  type?: 'text' | 'email' | 'password' | 'number';
  label?: string;
  error?: string;
  wrapperClassName?: string;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      type = 'text',
      label,
      error,
      id,
      className = '',
      wrapperClassName = '',
      disabled,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const inputId = id || generatedId;
    const errorId = `${inputId}-error`;

    const baseInputClasses = `
      block w-full px-3 py-2
      text-text-primary text-[12px]
      bg-surface-3
      border rounded-input
      placeholder-text-quaternary
      transition-all duration-150
      focus:outline-none focus:ring-2 focus:ring-offset-0
      disabled:opacity-50
      disabled:cursor-not-allowed
    `;

    const stateClasses = error
      ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20'
      : 'border-border-1 focus:border-accent focus:ring-accent-500/20';

    return (
      <div className={`${wrapperClassName}`.trim()}>
        {label && (
          <label
            htmlFor={inputId}
            className="block text-[12px] font-medium text-text-secondary mb-1.5"
          >
            {label}
          </label>
        )}
        <input
          ref={ref}
          type={type}
          id={inputId}
          disabled={disabled}
          aria-invalid={error ? 'true' : 'false'}
          aria-describedby={error ? errorId : undefined}
          className={`
            ${baseInputClasses}
            ${stateClasses}
            ${className}
          `.trim().replace(/\s+/g, ' ')}
          {...props}
        />
        {error && (
          <p
            id={errorId}
            className="mt-1 text-[11px] text-red-400"
            role="alert"
          >
            {error}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;
